import { inc } from 'alphacounter';
import { JSONPatch } from '../json-patch';
import { concurrency } from '../lib/concurrency';
import logging from '../log';
import { EMPTY_REV, REV_LENGTH, collapseLWWChanges, textConversion } from '../util';
const log = logging.tagColor('Syncer', 'Blue');
/**
 * Handle the syncing of a single document with the server.
 */
export function createDocSyncer(docConfig, tab, db, server, path) {
    const { type, hasText } = docConfig;
    const isOT = type === 'operational_transformation';
    const isLWW = type === 'last_write_wins';
    const docDB = db.doc(path);
    const { blockable, blockWhile } = concurrency(path);
    const api = {
        sync: blockable(sync),
        send: blockable(send),
        receive: blockable(receive),
        docDeleted: blockable(docDeleted),
    };
    return api;
    async function sync() {
        const since = await docDB.getLastRev({ committed: 1 });
        const localChanges = await docDB.getChanges({ committed: 0 });
        try {
            if (!since) {
                // First time syncing this document, get the doc from the server
                const [doc, rev] = await blockWhile(server.sendAfterAuthed('get', path, { rev: true }));
                await docDB.putDoc(doc, rev);
                tab.send({ path, doc, rev });
            }
            if (localChanges.length) {
                // Send pending changes to the server, also gets the latest changes from the server
                await send(localChanges);
            }
            else if (since) {
                // Get the changes from the server if we already have the doc
                const remoteChanges = await blockWhile(server.sendAfterAuthed('get', path, { since }));
                await receive(remoteChanges);
            }
        }
        catch (err) {
            const error = err;
            if (error.status === 404) {
                await docDeleted();
            }
            else {
                console.error(err);
                throw err;
            }
        }
    }
    /**
     * Send changes to the server and receive changes back. LWW and OT changes are treated differently in some ways.
     */
    async function send(changes) {
        if (!changes?.length) {
            // That was easy
            return [];
        }
        log('send to server:', changes.length === 1 ? changes[0] : changes);
        // Optimize LWW changes being sent to the server by collapsing them into a single change since many of the
        // operations are commutative. This can reduce the number of changes sent to the server.
        if (isLWW && changes.length > 1) {
            const change = collapseLWWChanges(changes);
            // Using a transaction to ensure we don't get halfway through this operation and lose data
            const trans = docDB.start();
            trans.deleteChanges(changes);
            trans.putChanges([change]);
            await trans.commit();
            changes = [change];
        }
        // Send the changes to the server. The server will return an array of the changes that were committed and any
        // other changes the server has that we don't. Our changes may have been transformed by the server and may have
        // been dropped by the server if after transformation the were empty/no-op.
        const returnedChanges = (await blockWhile(server.sendAfterAuthed('patch', path, changes)));
        log('received back from server:', returnedChanges.length === 1 ? returnedChanges[0] : returnedChanges);
        // The changes have been successfully saved to the server. We can delete them from the local database and store
        // the returned changes.
        await docDB.deleteChanges(changes);
        // If we got changes back, "receive" them. If we didn't get any changes back, delete the changes we sent because
        // they became no-ops.
        const receivedChanges = await receive(returnedChanges);
        if (receivedChanges.length) {
            await applyChangesToDocIfNeeded();
        }
        return receivedChanges;
    }
    /**
     * Receive changes from the server and apply them to the local database. These may be local changes we created and
     * sent to the server, or they may be changes from another client being sent to us. Then return all received changes
     * AND local changes that were transformed/updated by the received changes.
     */
    async function receive(receivedChanges) {
        if (!receivedChanges.length) {
            return receivedChanges;
        }
        // Map received changes by id so we can update local changes by the same id
        const receivedById = new Map();
        receivedChanges.forEach(c => {
            c.committed = 1; // We don't need to know the committed date, just that it was committed, 1 or 0
            if (c.id)
                receivedById.set(c.id, c);
        });
        const dbDoc = db.doc(path);
        // Get all of our locally stored changes for this document
        const storedChanges = await dbDoc.getChanges();
        // The stored changes that are already committed
        const syncedChanges = storedChanges.filter(c => c.committed);
        // The stored changes that are not committed and not being received right now
        let localChanges = storedChanges.filter(c => !c.committed && !receivedById.has(c.id));
        // The last committed revision we have (or 0 if we have none)
        const committedRev = syncedChanges[syncedChanges.length - 1]?.rev ?? (await dbDoc.getDoc())?.rev ?? EMPTY_REV;
        // Drop changes that we have already committed
        receivedChanges = receivedChanges.filter(c => !c.rev || c.rev > committedRev);
        if (!receivedChanges.length)
            return [];
        // Stop if there is a gap in what we have and what is being received, and resync
        if (isOT && receivedChanges[0].rev !== inc(committedRev, REV_LENGTH)) {
            sync();
            return [];
        }
        // Transform/update the local changes against the received changes.
        if (localChanges.length) {
            // Only OT will transform, LWW will just update the rev
            const committedPatch = isOT && new JSONPatch(receivedChanges.map(c => c.patch).flat(), hasText);
            let rev = receivedChanges[receivedChanges.length - 1].rev;
            localChanges = localChanges.map(change => {
                rev = inc(rev, REV_LENGTH);
                const patch = committedPatch ? committedPatch.transform(change.patch).ops : change.patch;
                return { ...change, rev, patch };
            });
        }
        // Save receieved and updated local changes
        const allSavedChanges = receivedChanges.concat(localChanges);
        await dbDoc.putChanges(receivedChanges.concat(localChanges));
        await applyChangesToDocIfNeeded();
        tab.send({ path, changes: allSavedChanges });
        return allSavedChanges;
    }
    async function docDeleted() {
        await db.doc(path).deleteDoc();
        tab.send({ path, deleted: true });
    }
    /**
     * Take committed changes and collapse them into the doc if there are enough changes.
     */
    async function applyChangesToDocIfNeeded() {
        const dbDoc = db.doc(path);
        const numChanges = await dbDoc.getChangeCount({ committed: 1 });
        if (numChanges > 20) {
            let trans = dbDoc.start();
            let [{ doc, rev }, changes] = await Promise.all([trans.getDoc(), trans.getChanges({ committed: 1 })]);
            if (hasText) {
                textConversion.revive[hasText](doc);
            }
            if (isOT && changes[0].rev !== inc(rev, REV_LENGTH)) {
                await trans.commit(); // Don't hold up
                changes = await server.sendAfterAuthed('get', path, { since: rev });
                trans = dbDoc.start();
            }
            const patch = changes.map(c => c.patch).flat();
            doc = new JSONPatch(patch, hasText).apply(doc);
            if (hasText) {
                textConversion.replace[hasText](doc);
            }
            trans.deleteChanges(changes);
            trans.putDoc(doc, changes.at(-1).rev);
            await trans.commit();
        }
    }
}
